import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { tradeRecords } from '@/mocks/tradingData';

// The whole terminal is driven by a single source of truth: tradeRecords.
// Add or edit trades there and both the price path and the stats below update together.

// Build the price path from each trade's entry and exit price.
const pricePath = tradeRecords.flatMap((t, idx) => [
  { i: idx * 2, price: t.entry },
  { i: idx * 2 + 1, price: t.exit },
]);

// Auto-fit the Y axis to the actual price range so the line is always fully visible.
const pathPrices = pricePath.map((d) => d.price);
const pathMin = Math.min(...pathPrices);
const pathMax = Math.max(...pathPrices);
const pathPad = Math.max((pathMax - pathMin) * 0.12, 1);
const yDomain: [number, number] = [pathMin - pathPad, pathMax + pathPad];

// Stats derived from the same records, so they can never drift out of sync.
const winCount = tradeRecords.filter((t) => t.profit > 0).length;
const lossCount = tradeRecords.length - winCount;
const winRatePct = `${((winCount / tradeRecords.length) * 100).toFixed(1)}%`;

// Small markers on the most recent trades, with labels on the latest one.
const recentTrades = tradeRecords.slice(-10);
const recentStartIndex = tradeRecords.length - recentTrades.length;
const recentDots = recentTrades.flatMap((t, k) => {
  const base = (recentStartIndex + k) * 2;
  return [
    { i: base, price: t.entry },
    { i: base + 1, price: t.exit },
  ];
});

const lastTrade = tradeRecords[tradeRecords.length - 1];
const lastEntryIndex = (tradeRecords.length - 1) * 2;
const lastExitIndex = lastEntryIndex + 1;

const statusModules = [
  { label: 'MARKET ANALYSIS', value: 'RUNNING', state: 'active' as const },
  { label: 'ENTRY CONDITIONS', value: 'WAITING', state: 'waiting' as const },
  { label: 'EXECUTION', value: 'MT5', state: 'engine' as const },
];

const footerStats = [
  { value: String(tradeRecords.length), label: 'TRADES' },
  { value: winRatePct, label: 'WIN RATE' },
  { value: `${winCount} / ${lossCount}`, label: 'WINS / LOSSES' },
];

function SystemTerminal() {
  return (
    <div className="w-full rounded-lg border border-background-300 bg-background-50 overflow-hidden">
      {/* terminal top bar */}
      <div className="flex items-center justify-between px-4 md:px-5 h-12 border-b border-background-200 bg-background-100">
        <div className="flex items-center gap-2.5">
          <span className="font-data text-[13px] font-semibold text-foreground-950">
            XAUUSD
          </span>
          <span className="font-data text-[11px] text-foreground-500">MT5</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-data tracking-widest text-foreground-600 hidden sm:inline">
            GOLD SYSTEM
          </span>
          <span className="w-1.5 h-1.5 rounded-full bg-profit-500"></span>
          <span className="text-[10px] font-data tracking-widest text-profit-600">ACTIVE</span>
        </div>
      </div>

      {/* chart */}
      <div className="relative h-[220px] md:h-[250px] px-1 pt-2">
        <div className="absolute inset-x-4 top-3 flex items-center justify-between pointer-events-none">
          <span className="text-[10px] font-data tracking-widest text-foreground-400">
            TRADE PRICE PATH
          </span>
          <span className="text-[10px] font-data tracking-widest text-foreground-400">
            {tradeRecords.length} CLOSED
          </span>
        </div>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={pricePath} margin={{ top: 34, right: 18, bottom: 10, left: 18 }}>
            <CartesianGrid stroke="oklch(var(--foreground-950) / 0.06)" vertical={false} />
            <XAxis dataKey="i" hide />
            <YAxis domain={yDomain} hide />
            <Line
              type="monotone"
              dataKey="price"
              stroke="oklch(var(--primary-500))"
              strokeWidth={2.2}
              dot={false}
              isAnimationActive
              animationDuration={2000}
            />
            {recentDots.map((d) => (
              <ReferenceDot
                key={`dot-${d.i}`}
                x={d.i}
                y={d.price}
                r={2.4}
                fill="oklch(var(--primary-500))"
                stroke="none"
                isFront
              />
            ))}
            <ReferenceDot
              x={lastEntryIndex}
              y={lastTrade.entry}
              r={4}
              fill="oklch(var(--primary-500))"
              stroke="oklch(var(--background-50))"
              strokeWidth={2}
              isFront
              label={{
                value: 'ENTRY',
                position: 'bottom',
                fill: 'oklch(var(--foreground-700))',
                fontSize: 9,
                fontFamily: 'var(--font-mono)',
              }}
            />
            <ReferenceDot
              x={lastExitIndex}
              y={lastTrade.exit}
              r={4}
              fill="oklch(var(--foreground-950))"
              stroke="oklch(var(--background-50))"
              strokeWidth={2}
              isFront
              label={{
                value: 'EXIT',
                position: 'top',
                fill: 'oklch(var(--foreground-700))',
                fontSize: 9,
                fontFamily: 'var(--font-mono)',
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* status modules */}
      <div className="grid grid-cols-3 border-t border-background-200">
        {statusModules.map((m, idx) => (
          <div
            key={m.label}
            className={`px-3 md:px-4 py-3.5 ${
              idx < statusModules.length - 1 ? 'border-r border-background-200' : ''
            } ${m.state === 'waiting' ? 'bg-accent-100' : ''}`}
          >
            <p className="text-[9px] md:text-[10px] font-data tracking-widest text-foreground-500 mb-1.5">
              {m.label}
            </p>
            <div className="flex items-center gap-1.5">
              {m.state === 'active' && (
                <span className="w-1.5 h-1.5 rounded-full bg-profit-500"></span>
              )}
              {m.state === 'waiting' && (
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 breathe"></span>
              )}
              <span
                className={`text-[13px] md:text-[15px] font-heading font-bold tracking-tight ${
                  m.state === 'waiting'
                    ? 'text-primary-700'
                    : m.state === 'active'
                      ? 'text-profit-600'
                      : 'text-foreground-950'
                }`}
              >
                {m.value}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* footer stats */}
      <div className="grid grid-cols-3 border-t border-background-200 bg-foreground-950">
        {footerStats.map((s, idx) => (
          <div
            key={s.label}
            className={`px-3 md:px-4 py-3.5 text-center ${
              idx < footerStats.length - 1 ? 'border-r border-background-50/10' : ''
            }`}
          >
            <p className="font-data text-[16px] md:text-[18px] font-semibold text-primary-400">
              {s.value}
            </p>
            <p className="text-[9px] font-data tracking-widest text-background-50/55 mt-1">
              {s.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SystemTerminal;